import api from './api';
import { downloadFromApi } from './download';

/**
 * Import intelligent (super administrateur) : photo, fichier Excel ou PDF vers n'importe quel type de
 * données d'une société (camions, chauffeurs, clients, transporteurs, fournisseurs).
 *
 * Chemins et formes de réponse : `backend/app/api/v1/smart_import.py`. Rien n'est calculé ici : le type
 * reconnu, le brouillon, les états et les phrases de refus viennent du serveur. Les noms des méthodes sont
 * ceux de `fleetImportApi` : le brouillon et le rapport de l'import de flotte les utilisent tels quels.
 */

const BASE = '/smart-import';
// Le serveur répond `{ success, data }` : `data` vaut `null` quand il n'y a rien (aucun import en cours).
const unwrap = (response) => {
  const corps = response?.data;
  return corps && typeof corps === 'object' && !Array.isArray(corps) && 'data' in corps ? corps.data : corps;
};

/** Photos, fichiers Excel ou CSV, PDF : ce que le sélecteur de fichiers propose. */
export const EXTENSIONS_IMPORT = 'image/*,.jpg,.jpeg,.png,.webp,.xlsx,.csv,.pdf';
/** États où le serveur travaille : on relit le travail tant qu'ils durent. */
export const ETATS_EN_COURS = ['queued', 'analyzing', 'confirmed', 'importing'];

export const smartImportApi = {
  /** Le travail en cours de la société cible (ou le dernier terminé), ou `null`. */
  async current(organizationId) {
    return unwrap(await api.get(`${BASE}/jobs/current`, { params: { organization_id: organizationId } })) || null;
  },

  async get(jobId) {
    return unwrap(await api.get(`${BASE}/jobs/${jobId}`));
  },

  /** Dépose un ou plusieurs fichiers ; l'analyse se poursuit en arrière-plan. */
  async upload(fichiers, organizationId) {
    const formulaire = new FormData();
    Array.from(fichiers).forEach((fichier) => formulaire.append('files', fichier));
    formulaire.append('organization_id', organizationId);
    return unwrap(
      await api.post(`${BASE}/jobs`, formulaire, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      }),
    );
  },

  async rows(jobId, { kind, state, q, page = 1, pageSize = 50 } = {}) {
    const params = { page, page_size: pageSize };
    if (kind) params.kind = kind;
    if (state) params.state = state;
    if (q) params.q = q;
    return unwrap(await api.get(`${BASE}/jobs/${jobId}/rows`, { params }));
  },

  async reportLines(jobId, { status, page = 1, pageSize = 50 } = {}) {
    const params = { page, page_size: pageSize };
    if (status) params.status = status;
    return unwrap(await api.get(`${BASE}/jobs/${jobId}/report-lines`, { params }));
  },

  /** Corrige des cellules (`values`) et/ou coche ou décoche la ligne (`included`). */
  async editRow(jobId, key, { values, included } = {}) {
    const corps = { key };
    if (values) corps.values = values;
    if (included !== undefined) corps.included = included;
    return unwrap(await api.patch(`${BASE}/jobs/${jobId}/rows`, corps));
  },

  async select(jobId, { included, kind, state, keys }) {
    const corps = { included };
    if (kind) corps.kind = kind;
    if (state) corps.state = state;
    if (keys) corps.keys = keys;
    return unwrap(await api.post(`${BASE}/jobs/${jobId}/selection`, corps));
  },

  /** Confirme l'import ; renvoyer deux fois ne relance rien. */
  async confirm(jobId, draftVersion) {
    return unwrap(await api.post(`${BASE}/jobs/${jobId}/confirm`, { draft_version: draftVersion }));
  },

  async cancel(jobId) {
    return unwrap(await api.post(`${BASE}/jobs/${jobId}/cancel`));
  },

  async report(jobId) {
    return downloadFromApi(`${BASE}/jobs/${jobId}/report`, {}, 'rapport-import-intelligent.xlsx');
  },
};

export default smartImportApi;
